/**
 * Mobile Mart - Theme Core JavaScript (Vanilla ES6)
 * Online Store 2.0 Compliant
 */

// Global Cart Manager for Shopify AJAX Cart Operations
window.CartManager = {
  isUpdating: false,

  // Add Item to Cart via AJAX (Ensures exactly 1 addition per click)
  async add(variantId, quantity = 1, buttonEl = null) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    let originalBtnText = '';
    if (buttonEl) {
      originalBtnText = buttonEl.innerHTML;
      buttonEl.innerHTML = `<span class="mm-spinner"></span> Adding...`;
      buttonEl.disabled = true;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: parseInt(quantity, 10) || 1
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Could not add product to cart');
      }

      const itemData = await response.json();

      // Refresh cart state in real time
      await this.refresh();

      if (buttonEl) {
        buttonEl.innerHTML = `✓ Added!`;
        setTimeout(() => {
          buttonEl.innerHTML = originalBtnText;
          buttonEl.disabled = false;
        }, 1200);
      }

      // Open Cart Drawer immediately
      window.dispatchEvent(new CustomEvent('mobilemart:cart:open'));
      
      return itemData;
    } catch (err) {
      console.error('Cart add error:', err);
      alert(err.message || 'Error adding product to cart. Please try again.');
      if (buttonEl) {
        buttonEl.innerHTML = originalBtnText;
        buttonEl.disabled = false;
      }
    } finally {
      this.isUpdating = false;
    }
  },

  // Change Item Quantity
  async change(lineIndex, quantity) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          line: lineIndex,
          quantity: parseInt(quantity, 10)
        })
      });

      if (!response.ok) throw new Error('Could not update cart quantity');
      const cart = await response.json();
      this.updateCartUI(cart);
    } catch (err) {
      console.error('Cart update error:', err);
    } finally {
      this.isUpdating = false;
    }
  },

  // Reset/Clear Entire Cart
  async clear() {
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Could not clear cart');
      const cart = await response.json();
      this.updateCartUI(cart);
    } catch (err) {
      console.error('Cart clear error:', err);
    } finally {
      this.isUpdating = false;
    }
  },

  // Fetch Cart JSON and Update UI
  async refresh() {
    try {
      const res = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
      if (res.ok) {
        const cart = await res.json();
        this.updateCartUI(cart);
        return cart;
      }
    } catch (err) {
      console.error('Cart refresh error:', err);
    }
  },

  // Update Cart Counters, Subtotals and Drawer Markup in Real Time
  updateCartUI(cart) {
    // 1. Update all badge counts
    const countBadges = document.querySelectorAll('[data-cart-count]');
    countBadges.forEach(el => {
      el.textContent = cart.item_count;
    });

    // 2. Format currency (UAE Dirhams)
    const formattedSubtotal = (cart.total_price / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    // 3. Update Subtotal elements
    const subtotalEls = document.querySelectorAll('[data-cart-subtotal]');
    subtotalEls.forEach(el => {
      el.textContent = `AED ${formattedSubtotal}`;
    });

    // 4. Update Free Shipping Progress Bar
    const progressFill = document.querySelector('.mm-progress-fill');
    const shippingText = document.querySelector('.mm-shipping-text');
    const shippingThreshold = (window.mmFreeShippingThreshold || 200) * 100; // in cents
    if (progressFill) {
      const pct = Math.min(100, Math.round((cart.total_price / shippingThreshold) * 100));
      progressFill.style.width = `${pct}%`;
    }
    if (shippingText) {
      const remainingAed = Math.max(0, (shippingThreshold - cart.total_price) / 100);
      if (remainingAed <= 0) {
        shippingText.innerHTML = '🎉 You unlocked <strong>FREE UAE DELIVERY</strong>!';
      } else {
        shippingText.innerHTML = `You are <strong>AED ${remainingAed.toFixed(2)}</strong> away from <strong>FREE UAE DELIVERY</strong>!`;
      }
    }

    // 5. Update Drawer Footer (Checkout Button) Visibility Real-Time
    const drawerFooter = document.getElementById('CartDrawerFooter') || document.querySelector('.mm-drawer-footer');
    if (drawerFooter) {
      drawerFooter.style.display = cart.item_count > 0 ? 'block' : 'none';
    }

    // 6. Update Upsells Visibility
    const upsellsWrap = document.querySelector('.mm-cart-upsells');
    if (upsellsWrap) {
      upsellsWrap.style.display = cart.item_count > 0 ? 'block' : 'none';
    }

    // 7. Render items in Drawer Container
    const container = document.querySelector('.mm-cart-items-container');
    if (!container) return;

    if (cart.item_count === 0 || !cart.items || cart.items.length === 0) {
      container.innerHTML = `
        <div style="padding: 48px 20px; text-align: center; color: var(--color-text-muted);">
          <div style="margin-bottom: 16px;">
            <svg width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" style="margin: 0 auto; color: #94a3b8;"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
          </div>
          <p style="font-weight: 700; font-size: 1.05rem; color: var(--color-primary); margin-bottom: 8px;">Your cart is empty</p>
          <p style="font-size: 0.85rem; margin-bottom: 24px;">Discover the latest smartphones, tablets & tech gear.</p>
          <a href="/collections/all" class="mm-btn mm-btn-primary" style="font-size: 0.85rem; padding: 10px 24px;">Start Shopping &rarr;</a>
        </div>
      `;
    } else {
      let itemsHtml = '';
      cart.items.forEach((item, idx) => {
        const linePrice = (item.final_line_price / 100).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });

        itemsHtml += `
          <div class="mm-cart-item" data-line="${idx + 1}">
            <img src="${item.image || ''}" alt="${item.title}" class="mm-cart-item-img">
            <div class="mm-cart-item-details">
              <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;">
                <div>
                  <a href="${item.url}" class="mm-cart-item-title">${item.product_title || item.title}</a>
                  ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="mm-cart-item-variant">${item.variant_title}</div>` : ''}
                </div>
                <button 
                  type="button" 
                  onclick="CartManager.change(${idx + 1}, 0)" 
                  class="mm-cart-remove-btn" 
                  title="Remove Item"
                  style="background: none; border: none; color: #94a3b8; cursor: pointer; padding: 4px; transition: color 0.2s;"
                  onmouseover="this.style.color='#ef4444'" 
                  onmouseout="this.style.color='#94a3b8'"
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                </button>
              </div>
              <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 8px;">
                <div style="display: flex; align-items: center; border: 1px solid var(--color-border); border-radius: 6px; overflow: hidden; background: #f8fafc;">
                  <button type="button" style="padding: 4px 10px; font-weight: bold; cursor: pointer; border: none; background: none;" onclick="CartManager.change(${idx + 1}, ${item.quantity - 1})">-</button>
                  <span style="padding: 0 8px; font-size: 0.85rem; font-weight: 700;">${item.quantity}</span>
                  <button type="button" style="padding: 4px 10px; font-weight: bold; cursor: pointer; border: none; background: none;" onclick="CartManager.change(${idx + 1}, ${item.quantity + 1})">+</button>
                </div>
                <div style="font-weight: 800; font-size: 0.95rem; color: var(--color-primary);">AED ${linePrice}</div>
              </div>
            </div>
          </div>
        `;
      });
      container.innerHTML = itemsHtml;
    }
  }
};

class MobileMartTheme {
  constructor() {
    this.init();
  }

  init() {
    this.initCartDrawer();
    this.initAjaxAddToCart();
    this.initHeroSlider();
    this.initQuickView();
    this.initCountdownTimers();
    this.initRecentlyViewed();
    this.initProductGallery();
    this.initStickyAddToCart();
  }

  // Cart Drawer Controller
  initCartDrawer() {
    const drawer = document.getElementById('CartDrawer');
    const overlay = document.getElementById('CartDrawerOverlay');
    const openBtns = document.querySelectorAll('[data-cart-drawer-trigger]');
    const closeBtns = document.querySelectorAll('[data-cart-drawer-close]');

    if (!drawer || !overlay) return;

    const openDrawer = () => {
      drawer.classList.add('is-active');
      overlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    };

    const closeDrawer = () => {
      drawer.classList.remove('is-active');
      overlay.classList.remove('is-active');
      document.body.style.overflow = '';
    };

    openBtns.forEach(btn => btn.addEventListener('click', (e) => {
      e.preventDefault();
      openDrawer();
    }));

    closeBtns.forEach(btn => btn.addEventListener('click', closeDrawer));
    overlay.addEventListener('click', closeDrawer);

    // Global event listener for opening cart after AJAX addition
    window.addEventListener('mobilemart:cart:open', openDrawer);
  }

  // AJAX Add to Cart Buttons
  initAjaxAddToCart() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-add-to-cart-ajax]');
      if (!btn) return;

      e.preventDefault();
      const variantId = btn.getAttribute('data-variant-id');
      const qty = parseInt(btn.getAttribute('data-quantity') || '1', 10);

      if (variantId) {
        window.CartManager.add(variantId, qty, btn);
      }
    });
  }

  // Interactive Hero Slider
  initHeroSlider() {
    const slider = document.querySelector('.mm-hero-slider');
    if (!slider) return;

    const slides = slider.querySelectorAll('.mm-hero-slide');
    const dots = slider.querySelectorAll('[data-slide-index]');
    const prevBtn = slider.querySelector('[data-slide-prev]');
    const nextBtn = slider.querySelector('[data-slide-next]');

    if (slides.length <= 1) return;

    let currentSlide = 0;
    let slideInterval = null;

    const goToSlide = (index) => {
      slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
      dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
      currentSlide = index;
    };

    const nextSlide = () => {
      goToSlide((currentSlide + 1) % slides.length);
    };

    const prevSlide = () => {
      goToSlide((currentSlide - 1 + slides.length) % slides.length);
    };

    const startAutoplay = () => {
      stopAutoplay();
      slideInterval = setInterval(nextSlide, 6000);
    };

    const stopAutoplay = () => {
      if (slideInterval) clearInterval(slideInterval);
    };

    dots.forEach((dot, idx) => {
      dot.addEventListener('click', () => {
        goToSlide(idx);
        startAutoplay();
      });
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        prevSlide();
        startAutoplay();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        nextSlide();
        startAutoplay();
      });
    }

    slider.addEventListener('mouseenter', stopAutoplay);
    slider.addEventListener('mouseleave', startAutoplay);

    startAutoplay();
  }

  // Quick View Modal
  initQuickView() {
    const modal = document.getElementById('QuickViewModal');
    if (!modal) return;

    document.querySelectorAll('[data-quick-view-trigger]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const handle = btn.getAttribute('data-product-handle');
        if (!handle) return;

        try {
          const res = await fetch(`/products/${handle}?view=quick-view`);
          if (res.ok) {
            const html = await res.text();
            const container = modal.querySelector('.mm-quick-view-content');
            if (container) container.innerHTML = html;
            modal.classList.add('is-active');
          }
        } catch (err) {
          console.error('Quick view error:', err);
        }
      });
    });

    const closeBtn = modal.querySelector('[data-modal-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => modal.classList.remove('is-active'));
    }
  }

  // Countdown Timers for Deals
  initCountdownTimers() {
    const timers = document.querySelectorAll('[data-countdown-end]');
    timers.forEach(timer => {
      const endTimeStr = timer.getAttribute('data-countdown-end');
      if (!endTimeStr) return;
      const target = new Date(endTimeStr).getTime();

      const update = () => {
        const now = new Date().getTime();
        const distance = target - now;

        if (distance < 0) {
          timer.innerHTML = '<span class="mm-deal-expired">Deal Ended</span>';
          return;
        }

        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const mins = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const secs = Math.floor((distance % (1000 * 60)) / 1000);

        const dEl = timer.querySelector('.timer-days');
        const hEl = timer.querySelector('.timer-hours');
        const mEl = timer.querySelector('.timer-mins');
        const sEl = timer.querySelector('.timer-secs');

        if (dEl) dEl.textContent = String(days).padStart(2, '0');
        if (hEl) hEl.textContent = String(hours).padStart(2, '0');
        if (mEl) mEl.textContent = String(mins).padStart(2, '0');
        if (sEl) sEl.textContent = String(secs).padStart(2, '0');
      };

      update();
      setInterval(update, 1000);
    });
  }

  // Product Gallery & Thumbnails
  initProductGallery() {
    const mainImg = document.querySelector('[data-main-product-image]');
    const thumbs = document.querySelectorAll('[data-gallery-thumbnail]');

    if (!mainImg || !thumbs.length) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const newSrc = thumb.getAttribute('data-media-src');
        if (newSrc) {
          mainImg.src = newSrc;
          thumbs.forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        }
      });
    });
  }

  // Sticky Add to Cart (Product Page)
  initStickyAddToCart() {
    const stickyBar = document.querySelector('.mm-sticky-add-to-cart');
    const mainAddToCart = document.querySelector('.mm-product-form-submit');

    if (!stickyBar || !mainAddToCart) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          stickyBar.classList.add('is-visible');
        } else {
          stickyBar.classList.remove('is-visible');
        }
      });
    }, { rootMargin: '0px 0px -100px 0px' });

    observer.observe(mainAddToCart);
  }

  // Recently Viewed Products
  initRecentlyViewed() {
    const currentHandle = document.body.getAttribute('data-product-handle');
    if (!currentHandle) return;

    let recents = JSON.parse(localStorage.getItem('mm_recently_viewed') || '[]');
    recents = recents.filter(h => h !== currentHandle);
    recents.unshift(currentHandle);
    if (recents.length > 8) recents.pop();

    localStorage.setItem('mm_recently_viewed', JSON.stringify(recents));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.mobileMart = new MobileMartTheme();
});
